import './download-link.scss';
import Base from '../base/base';

/** Shown in place of the link's own label while the file is being fetched */
const BUSY_LABEL = 'Preparing…';

/** Webflow prefixes uploaded assets with a 24-character hex id and an underscore */
const WEBFLOW_ASSET_PREFIX = /^[0-9a-f]{24}_/i;

/** Characters a filesystem will not take in a name */
const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]/g;

interface DownloadLinkOptions {
  /** Selector for the links to take over */
  selector?: string;
  /** Selector, within a link, for the text to rename while busy */
  labelSelector?: string;
  /** Selector for the element whose text names the saved file */
  filenameFrom?: string;
  /** Appended to that text, e.g. 'Spec Sheet' */
  filenameSuffix?: string;
  debug?: boolean;
  [key: string]: any;
}

interface DownloadLinkConfig {
  selector: string;
  labelSelector: string;
  filenameFrom: string;
  filenameSuffix: string;
}

/**
 * DownloadLink
 *
 * Turns a link to a file into a download instead of a navigation.
 *
 * The `download` attribute cannot do this on its own: the assets live on
 * Webflow's CDN, a different origin from the site, and browsers ignore
 * `download` cross-origin. That CDN does send `access-control-allow-origin: *`,
 * so the file can be fetched, wrapped in a blob URL, and handed to a synthetic
 * `<a download>` — which is what produces the save dialog.
 *
 * The file is read in full before the dialog appears, so the link carries a
 * pending state while that happens. No percentage: the CDN exposes no
 * `content-length` to script, so there would be no total to count against.
 *
 * Anything that goes wrong — a rejected fetch, a non-OK response, a browser
 * without `createObjectURL` — falls through to navigating to the file, which is
 * the behavior the link had to begin with.
 *
 * Nothing here is specific to a page or a file type; point `selector` at the
 * links a page wants handled.
 */
export default class DownloadLink extends Base {
  static readonly BUSY_CLASS = 'is-downloading';

  private config: DownloadLinkConfig;

  private links: HTMLAnchorElement[] = [];
  private listeners: { element: HTMLAnchorElement; handler: EventListener }[] = [];
  /** Links with a fetch in flight, so a second click is ignored */
  private inFlight = new Set<HTMLAnchorElement>();

  constructor(options: DownloadLinkOptions = {}) {
    super(options.debug || false);

    this.config = {
      selector: 'a[href$=".pdf"]',
      labelSelector: '.kl-btn-txt',
      filenameFrom: '',
      filenameSuffix: '',
      ...options
    };

    this.init();
  }

  init(): void {
    this.links = Array.from(document.querySelectorAll<HTMLAnchorElement>(this.config.selector));

    if (!this.links.length) {
      this.log('No links matched, skipping', this.config.selector);
      return;
    }

    this.links.forEach((link) => {
      // An unpublished asset still reads as `#`; leave those alone so the link
      // behaves exactly as it does today rather than fetching the page itself
      const href = link.getAttribute('href') || '';
      if (!href || href === '#') return;

      const handler = (event: Event) => this.onClick(event, link);
      link.addEventListener('click', handler);
      this.listeners.push({ element: link, handler });
    });

    this.log(`Handling ${this.listeners.length} of ${this.links.length} matched link(s)`);
  }

  private async onClick(event: Event, link: HTMLAnchorElement): Promise<void> {
    if (this.inFlight.has(link)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const href = link.href;

    this.inFlight.add(link);
    this.setBusy(link, true);

    try {
      const response = await fetch(href);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const blob = await response.blob();
      this.saveBlob(blob, this.filenameFor(href));
      this.log('Downloaded', href);
    } catch (error) {
      this.log('Download failed, navigating instead', error);
      this.navigate(href);
    } finally {
      this.inFlight.delete(link);
      this.setBusy(link, false);
    }
  }

  /**
   * Pending state: the class and `aria-busy` for anything watching, and the
   * label swapped so the wait is legible. All three revert together, whichever
   * way the fetch settles.
   */
  private setBusy(link: HTMLAnchorElement, busy: boolean): void {
    link.classList.toggle(DownloadLink.BUSY_CLASS, busy);

    if (busy) {
      link.setAttribute('aria-busy', 'true');
    } else {
      link.removeAttribute('aria-busy');
    }

    const label = link.querySelector<HTMLElement>(this.config.labelSelector);
    if (!label) return;

    if (busy) {
      label.dataset.idleLabel = label.textContent || '';
      label.textContent = BUSY_LABEL;
    } else {
      label.textContent = label.dataset.idleLabel ?? label.textContent;
      delete label.dataset.idleLabel;
    }
  }

  private saveBlob(blob: Blob, filename: string): void {
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      throw new Error('createObjectURL unavailable');
    }

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  /**
   * Name the file after the page rather than the CDN, so a downloads folder
   * reads as "The Grande Fjord - Spec Sheet.pdf". Falls back to the asset's own
   * name, minus the hex id Webflow prefixes to every upload.
   */
  private filenameFor(href: string): string {
    const fallback = this.assetName(href);
    if (!this.config.filenameFrom) return fallback;

    const source = document.querySelector(this.config.filenameFrom);
    const text = (source?.textContent || '').trim();
    if (!text) return fallback;

    const extension = fallback.includes('.') ? fallback.slice(fallback.lastIndexOf('.')) : '';
    const parts = [text, this.config.filenameSuffix].filter(Boolean).join(' - ');
    return `${parts.replace(ILLEGAL_FILENAME_CHARS, '').trim()}${extension}`;
  }

  private assetName(href: string): string {
    const path = href.split('?')[0].split('#')[0];
    const base = decodeURIComponent(path.slice(path.lastIndexOf('/') + 1));
    return base.replace(WEBFLOW_ASSET_PREFIX, '') || 'download';
  }

  /** The one way out when the fetch cannot deliver, and the seam the tests watch */
  protected navigate(href: string): void {
    window.location.assign(href);
  }

  destroy(): void {
    this.listeners.forEach(({ element, handler }) => element.removeEventListener('click', handler));
    this.listeners = [];
    this.inFlight.clear();
    this.links = [];
  }
}
